import { arrayToIdKeyedObject } from '@sequencehq/utils'
import {
  Duration,
  durationsToDateAdapter,
  isAfter,
  isBefore,
  sub
} from '@sequencehq/utils/dates'
import { useCubeContext } from 'Cube/communication/internal/cube.domain.context'
import { DuplicatedPhaseData, duplicatePhase } from 'Cube/utils/duplicatePhase'
import { CubeShortcut, cubeUpdateShortcuts } from 'Cube/utils/updateShortcuts'
import { useCallback, useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { match } from 'ts-pattern'

export const useCubeShortcuts = () => {
  const cubeCtx = useCubeContext()
  const [searchParams, setSearchParams] = useSearchParams()

  const latestPhase = useMemo(() => {
    return cubeCtx.queries.orderedPhases.at(-1)
  }, [cubeCtx.queries.orderedPhases])

  const highlightPhase = useCallback(
    (phaseId: string) => {
      cubeCtx.mutators.updateEditor({
        highlightedPhases: [phaseId]
      })
    },
    [cubeCtx.mutators.updateEditor]
  )

  /**
   * Control when to load/reload.
   */
  const createNewPhase = useCallback(
    ({
      newPhaseData,
      latestPhaseId,
      latestPhaseDuration
    }: {
      newPhaseData: DuplicatedPhaseData
      latestPhaseId?: string
      latestPhaseDuration?: Duration
    }) => {
      cubeCtx.mutators.updateData({
        phases: {
          ...(latestPhaseId && latestPhaseDuration
            ? {
                [latestPhaseId]: {
                  duration: latestPhaseDuration
                }
              }
            : {}),
          [newPhaseData.phase.id]: {
            ...newPhaseData.phase
          }
        },
        common: {
          phaseIds: [
            ...cubeCtx.queries.rawData.data.common.phaseIds,
            newPhaseData.phase.id
          ]
        },
        prices: arrayToIdKeyedObject(newPhaseData.prices),
        discounts: arrayToIdKeyedObject(newPhaseData.discounts),
        minimums: arrayToIdKeyedObject(newPhaseData.minimums)
      })
    },
    [cubeCtx]
  )

  const applyCubeShortcut = useCallback(
    (cubeShortcut: CubeShortcut) => {
      match(cubeShortcut)
        .with({ type: 'upgrade' }, cs => {
          /**
           * When upgrading, we will start a new phase, duplicating the
           * last phase in the schedule.
           */
          if (!latestPhase) {
            return
          }

          const newDurationForFinalPhase = durationsToDateAdapter.toDuration(
            latestPhase.dates.absolute.start
          )(sub(cs.startDate, { days: 1 }))

          if (!newDurationForFinalPhase) {
            return
          }

          const duplicatePhaseData = duplicatePhase({
            phase: latestPhase
          })

          createNewPhase({
            newPhaseData: {
              ...duplicatePhaseData,
              phase: {
                ...duplicatePhaseData.phase,
                name: 'Upgrade',
                duration: {
                  years: 1,
                  days: -1
                }
              }
            },
            latestPhaseId: latestPhase.id,
            latestPhaseDuration: newDurationForFinalPhase
          })
          highlightPhase(duplicatePhaseData.phase.id)
        })
        .with({ type: 'renew' }, cs => {
          /**
           * When renewing, we will take the phase to renew, duplicate it, and
           * append it to the end of the schedule.
           */
          const phaseToRenew = cubeCtx.queries.orderedPhases.at(cs.phaseIndex)

          if (!phaseToRenew) {
            return
          }

          const duplicatePhaseData = duplicatePhase({
            phase: phaseToRenew
          })

          createNewPhase({
            newPhaseData: {
              ...duplicatePhaseData,
              phase: {
                ...duplicatePhaseData.phase,
                name: 'Renewal'
              }
            }
          })
          highlightPhase(duplicatePhaseData.phase.id)
        })
        .with({ type: 'cancel' }, cs => {
          /**
           * When cancelling, we want to find the phase that the cancellation
           * will fall in, set the duration of that phase to match up to
           * the new cancellation date, and then trim any following
           * phases.
           */
          const phaseToEndOn = cubeCtx.queries.orderedPhases.find(p => {
            if (!p.dates.absolute.start) {
              return
            }

            return (
              isBefore(p.dates.absolute.start, cs.endDate) &&
              (!p.dates.absolute.end ||
                isAfter(p.dates.absolute.end, cs.endDate))
            )
          })

          if (!phaseToEndOn) {
            return
          }

          const newFinalPhaseIndex = cubeCtx.queries.orderedPhases.findIndex(
            p => p.id === phaseToEndOn.id
          )
          const newDurationForFinalPhase = durationsToDateAdapter.toDuration(
            phaseToEndOn.dates.absolute.start
          )(cs.endDate)

          cubeCtx.mutators.updateData({
            common: {
              phaseIds: cubeCtx.queries.rawData.data.common.phaseIds.slice(
                0,
                newFinalPhaseIndex + 1
              )
            },
            phases: {
              [phaseToEndOn.id]: {
                duration: newDurationForFinalPhase
              }
            }
          })
          highlightPhase(phaseToEndOn.id)
        })
    },
    [
      cubeCtx.mutators.updateData,
      createNewPhase,
      highlightPhase,
      latestPhase,
      cubeCtx.queries.orderedPhases,
      cubeCtx.queries.rawData.data.common.phaseIds
    ]
  )

  /**
   * Apply any cube shortcut found, once the schedule has been loaded in.
   */
  useEffect(() => {
    const cubeShortcutUuid = searchParams.get(cubeUpdateShortcuts.queryParam)

    if (
      cubeCtx.queries.rawData.data.common.id !== 'uninitialized' &&
      cubeShortcutUuid
    ) {
      /**
       * It is possible to configure Cube to apply a preset modification to
       * the phases/schedule on load via the 'cube shortcut' mechanism. This bridges
       * the gap until we have schedule versions and all kinds of BE gubbins
       */
      const cubeShortcutUuid = searchParams.get(cubeUpdateShortcuts.queryParam)
      const cubeShortcut = cubeShortcutUuid
        ? cubeUpdateShortcuts.get(cubeShortcutUuid)
        : null

      if (cubeShortcut) {
        applyCubeShortcut(cubeShortcut)
      }

      searchParams.delete(cubeUpdateShortcuts.queryParam)
      /**
       * Reset the search params, with the shortcut uuid removed.
       */
      setSearchParams(searchParams)
    }
  }, [cubeCtx, applyCubeShortcut, searchParams, setSearchParams])

  return {
    applyCubeShortcut
  }
}
